import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmSerialTypeClient, FirearmSerialTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmSerialTypeClient = () => new FirearmSerialTypeClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns
export const useFirearmSerialTypes = () =>
  useQuery({
    queryKey: ['firearmSerialTypes'],
    queryFn: async () => {
      const response = await getFirearmSerialTypeClient().searchFirearmSerialTypes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmSerialTypeSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmSerialTypeClient().searchFirearmSerialTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmSerialTypeById = (id: string) =>
  useQuery({
    queryKey: ['firearmSerialType', id],
    queryFn: async () => {
      const response = await getFirearmSerialTypeClient().getFirearmSerialTypeById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmSerialType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmSerialTypeDTO) => {
      await getFirearmSerialTypeClient().createFirearmSerialType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmSerialTypes'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmSerialType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmSerialTypeDTO }) => {
      await getFirearmSerialTypeClient().updateFirearmSerialType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmSerialTypes'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmSerialType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmSerialTypeClient().deleteFirearmSerialType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmSerialTypes'] });
    },
  });
};